import React, { useState, useEffect, useRef } from 'react';
import { URLS } from './URLS';

// === HELPER: Определяне на възможните оператори според типа ===
const getOperators = (fieldType) => {
    if (fieldType.includes('Date') || fieldType.includes('Time')) {
        return [
            { label: 'After / From (>=)', op: 'gte', input: 'date' },
            { label: 'Before / To (<=)', op: 'lte', input: 'date' },
            { label: 'Exact Date', op: 'exact', input: 'date' },
            { label: 'Last X days (Dynamic)', op: 'last_n_days', input: 'number' }
        ];
    } else if (fieldType.includes('Float') || fieldType.includes('Integer') || fieldType.includes('Number')) {
        return [
            { label: 'Equals (=)', op: 'exact', input: 'number' },
            { label: 'Greater than (>)', op: 'gt', input: 'number' },
            { label: 'Less than (<)', op: 'lt', input: 'number' }
        ];
    } else if (fieldType.includes('Boolean')) {
        return [
            { label: 'Is', op: 'exact', input: 'select', options: ['True', 'False'] }
        ];
    } else {
        // Default (String)
        return [
            { label: 'Contains', op: 'icontains', input: 'text' },
            { label: 'Exact Match', op: 'iexact', input: 'text' },
            { label: 'In list (напр. PR, QA)', op: 'in', input: 'text' },
            { label: 'Contains any (напр. fix, bug)', op: 'icontains_any', input: 'text' }
        ];
    }
};

// === COMPONENT: Tree Item (Updated with Filter Button) ===
const FieldTreeItem = ({ field, allFields, onExpand, onToggleDim, selectedDims, onAddToSum, onAddFilter, level = 0 }) => {
    const children = allFields.filter(f => 
        f.id.startsWith(field.id + '__') && 
        f.id.split('__').length === field.id.split('__').length + 1
    );
    const isExpanded = children.length > 0;
    const isNumeric = field.type.includes('Float') || field.type.includes('Integer') || field.is_virtual;

    return (
        <div style={{ marginLeft: `${level * 20}px`, borderLeft: level > 0 ? '1px dashed #ccc' : 'none' }}>
            <div className="field-row" style={{ display: 'flex', alignItems: 'center', padding: '4px 0', fontSize: '13px' }}>
                <div style={{ width: '20px', textAlign: 'center' }}>
                    {field.is_relation && (
                        <button onClick={() => onExpand(field)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#007bff', fontWeight: 'bold' }}>
                            {isExpanded ? '▼' : '▶'}
                        </button>
                    )}
                </div>

                <input 
                    type="checkbox" 
                    checked={selectedDims.includes(field.id)}
                    onChange={() => onToggleDim(field.id)}
                    style={{ marginRight: '6px' }}
                />
                
                <span style={{ color: field.is_relation ? '#222' : '#555', marginRight: 'auto' }}>
                    {field.label.split('->').pop().trim()}
                </span>

                {/* ACTION BUTTONS */}
                <div style={{ display: 'flex', gap: '5px' }}>
                    {/* Filter Button (Y) */}
                    {!field.is_relation && (
                        <button 
                            onClick={() => onAddFilter(field)}
                            title="Add as Filter"
                            style={{ border: '1px solid #6c757d', background: '#fff', color: '#6c757d', borderRadius: '3px', cursor: 'pointer', fontSize: '10px', padding: '1px 5px' }}
                        >
                            Filter
                        </button>
                    )}

                    {/* Sum Button (∑) */}
                    {isNumeric && (
                        <button 
                            onClick={() => onAddToSum(field)}
                            title="Add to Aggregation"
                            style={{ border: '1px solid #28a745', background: '#e8f5e9', color: '#28a745', borderRadius: '3px', cursor: 'pointer', fontSize: '10px', padding: '1px 5px' }}
                        >
                            +∑
                        </button>
                    )}
                </div>
            </div>

            {isExpanded && children.map(child => (
                <FieldTreeItem 
                    key={child.id} 
                    field={child} 
                    allFields={allFields} 
                    onExpand={onExpand}
                    onToggleDim={onToggleDim}
                    selectedDims={selectedDims}
                    onAddToSum={onAddToSum}
                    onAddFilter={onAddFilter}
                    level={level + 1}
                />
            ))}
        </div>
    );
};

// === MAIN COMPONENT ===
const DynamicReportEngine = ({ urls, user }) => {
    // --- STATE ---
    const [baseEntity, setBaseEntity] = useState("Project");
    const [availableFields, setAvailableFields] = useState([]); 
    const [loadingMetadata, setLoadingMetadata] = useState(false);
    
    // Configuration
    const [selectedDimensions, setSelectedDimensions] = useState([]); 
    const [activeFilters, setActiveFilters] = useState([]); // [{ id, fieldId, label, type, operator, value, isExclude }]
    const [customSums, setCustomSums] = useState([]); 
    const [sumBuilder, setSumBuilder] = useState([]); 
    const [pivotField, setPivotField] = useState("");

    const [bucketConfigs, setBucketConfigs] = useState([]); // Пази конфигурациите за категории

    // Results
    const [results, setResults] = useState([]);
    const [columnOrder, setColumnOrder] = useState([]);
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);
    const [loadingReport, setLoadingReport] = useState(false);
    const [error, setError] = useState(null);

    const [tempSumName, setTempSumName] = useState("");

    // Временен state за формата за Bucketing (Обединяване)
    const [showBucketForm, setShowBucketForm] = useState(false);
    const [bucketDraft, setBucketDraft] = useState({ source_field: '', new_column: '', buckets: [] });

    const [savedPresets, setSavedPresets] = useState([]);
    const [selectedPresetId, setSelectedPresetId] = useState("");

    // === АВТОМАТИЗАЦИЯ STATE ===
    const [isAutomated, setIsAutomated] = useState(false);
    const [autoEmails, setAutoEmails] = useState("");
    const [autoFreq, setAutoFreq] = useState("daily"); // daily, weekly, monthly
    const [autoTime, setAutoTime] = useState("08:00");
    const [autoDayOfWeek, setAutoDayOfWeek] = useState("1"); // 1=Понеделник
    const [autoDayOfMonth, setAutoDayOfMonth] = useState("1");

    const generateCronString = () => {
        if (!isAutomated || !autoEmails) return "";
        const [hour, minute] = autoTime.split(":");
        
        if (autoFreq === 'daily') return `${minute} ${hour} * * *`;
        if (autoFreq === 'weekly') return `${minute} ${hour} * * ${autoDayOfWeek}`;
        if (autoFreq === 'monthly') return `${minute} ${hour} ${autoDayOfMonth} * *`;
        return "";
    };



    useEffect(() => {
        if (results && results.length > 0) {
            const currentKeys = Object.keys(results[0]);
            
            // 1. Ако нямаме никаква подредба ИЛИ сме махнали стари колони -> презаписваме наново
            if (columnOrder.length === 0 || !columnOrder.every(col => currentKeys.includes(col))) {
                setColumnOrder(currentKeys);
            } else {
                // 2. === ТУК БЕШЕ ПРОБЛЕМЪТ ===
                // Ако сме ДОБАВИЛИ нова колона (като втората ти сума), тя липсва в columnOrder.
                // Намираме кои са новите колони и ги закачаме в края на таблицата.
                const newColumns = currentKeys.filter(key => !columnOrder.includes(key));
                if (newColumns.length > 0) {
                    setColumnOrder(prev => [...prev, ...newColumns]);
                }
            }
        }
    }, [results]);

    useEffect(() => {
        // Зареждаме метаданните за Project по подразбиране
        loadMetadataForModel("Project", true);

        // Дърпаме запазените репорти
        fetch(`${URLS.aj_get_report_presets}?user_id=${user.id}`, { cache: 'no-store' })
            .then(res => res.json())
            .then(data => {
                if (data.presets) setSavedPresets(data.presets);
            })
            .catch(err => console.error("Error loading presets:", err));
    }, []);


    const handleSavePreset = (isUpdate = false) => {
        if (!user || !user.id) {
            alert("Липсва потребителски профил!");
            return;
        }

        let name = "";
        let presetIdToSend = null;

        if (isUpdate && selectedPresetId) {
            const currentPreset = savedPresets.find(p => p.id.toString() === selectedPresetId);
            name = currentPreset ? currentPreset.name : "Обновен репорт";
            presetIdToSend = selectedPresetId;
        } else {
            name = prompt("Въведи име на новия репорт:");
            if (!name) return;
        }

        // ПАКЕТИРАМЕ НАСТРОЙКИТЕ (добавяме automation, за да ги възстановим визуално после)
        const configData = {
            selectedDimensions, activeFilters, customSums, bucketConfigs, columnOrder,
            automation: { isAutomated, autoEmails, autoFreq, autoTime, autoDayOfWeek, autoDayOfMonth }
        };

        const payload = { 
            user_id: user.id, 
            name: name, 
            base_entity: baseEntity, 
            config_data: configData,
            // НОВО: Пращаме ги отделно към бекенда, за да ги запише в Celery
            emails: isAutomated ? autoEmails : "",
            cron_string: generateCronString()
        };

        if (presetIdToSend) payload.preset_id = presetIdToSend;

        fetch(URLS.aj_save_report_preset, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
        })
        .then(res => res.json())
        .then(data => {
            if (data.status === 'success') {
                if (data.action === 'updated') {
                    alert("Промените са запазени успешно!");
                    setSavedPresets(prev => prev.map(p => 
                        p.id.toString() === presetIdToSend ? { ...p, config_data: configData } : p
                    ));
                } else {
                    alert("Новият шаблон е запазен!");
                    setSavedPresets(prev => [...prev, { id: data.id, name, base_entity: baseEntity, config_data: configData }]);
                    setSelectedPresetId(data.id.toString());
                }
            } else {
                alert("Грешка: " + data.error);
            }
        }).catch(err => console.error(err));
    };

    const handleLoadPreset = async (e) => {
        const pId = e.target.value;
        setSelectedPresetId(pId);
        if (!pId) return;

        const preset = savedPresets.find(p => p.id.toString() === pId);
        if (!preset) return;

        setBaseEntity(preset.base_entity);
        setResults([]);
        setLoadingMetadata(true);

        try {
            // 1. Декодираме конфигурацията
            let cfg = preset.config_data;
            if (typeof cfg === 'string') cfg = JSON.parse(cfg);
            if (typeof cfg === 'string') cfg = JSON.parse(cfg); // Защита от двойно стрингифициране

            // 2. Събираме ID-тата на ВСИЧКИ използвани полета в репорта
            const allUsedIds = [
                ...(cfg.selectedDimensions || []),
                ...(cfg.activeFilters || []).map(f => f.fieldId),
                ...(cfg.customSums || []).flatMap(s => s.fields),
                ...(cfg.bucketConfigs || []).map(b => b.source_field)
            ].filter(Boolean);

            // 3. Изчисляваме кои "родителски" клони трябва да разгънем
            // Напр. за "project__client__name" ще извади "project" и "project__client"
            const pathsToExpand = new Set();
            allUsedIds.forEach(id => {
                const parts = id.split('__');
                let currentPath = '';
                // Вземаме всички части без последната (защото последната е самото поле)
                for (let i = 0; i < parts.length - 1; i++) {
                    currentPath = currentPath ? `${currentPath}__${parts[i]}` : parts[i];
                    pathsToExpand.add(currentPath);
                }
            });

            // Сортираме ги по дължина, за да разгъваме първо корена, после децата
            const pathsArray = Array.from(pathsToExpand).sort((a, b) => a.split('__').length - b.split('__'));

            // 4. Дърпаме първо базовия модел (корена)
            const rootRes = await fetch(`${URLS.aj_get_report_metadata}?model=${preset.base_entity}`);
            const rootData = await rootRes.json();
            let accumulatedFields = rootData.fields || [];

            // 5. Итеративно (едно след друго) дърпаме нужните под-клони
            for (const path of pathsArray) {
                // Намираме полето в досегашния масив, за да му вземем related_model
                const fieldNode = accumulatedFields.find(f => f.id === path);
                
                if (fieldNode && fieldNode.related_model) {
                    const childRes = await fetch(`${URLS.aj_get_report_metadata}?model=${fieldNode.related_model}`);
                    const childData = await childRes.json();
                    
                    // Форматираме децата с префикса на родителя
                    const prefixedFields = (childData.fields || []).map(child => ({
                        ...child,
                        id: `${path}__${child.id}`,
                        label: child.label,
                        related_model: child.related_model
                    }));
                    
                    // Добавяме ги към общия списък (без дубликати)
                    const existingIds = new Set(accumulatedFields.map(f => f.id));
                    const uniqueNew = prefixedFields.filter(f => !existingIds.has(f.id));
                    accumulatedFields = [...accumulatedFields, ...uniqueNew];
                }
            }

            // 6. ОБНОВЯВАМЕ ВСИЧКО НАВЕДНЪЖ
            setAvailableFields(accumulatedFields);
            setSelectedDimensions(cfg.selectedDimensions || []);
            setActiveFilters(cfg.activeFilters || []);
            setCustomSums(cfg.customSums || []);
            setBucketConfigs(cfg.bucketConfigs || []);
            setColumnOrder(cfg.columnOrder || []);
            // ВЪЗСТАНОВЯВАНЕ НА АВТОМАТИЗАЦИЯТА
            const auto = cfg.automation || {};
            setIsAutomated(auto.isAutomated || false);
            setAutoEmails(auto.autoEmails || "");
            setAutoFreq(auto.autoFreq || "daily");
            setAutoTime(auto.autoTime || "08:00");
            setAutoDayOfWeek(auto.autoDayOfWeek || "1");
            setAutoDayOfMonth(auto.autoDayOfMonth || "1");

        } catch (err) {
            console.error("Грешка при автоматичното разгъване на шаблона:", err);
            alert("Възникна грешка при зареждането на структурата на шаблона.");
        } finally {
            setLoadingMetadata(false);
        }
    };

    // Логика за размяна на колоните
    const handleColumnSort = () => {
        if (dragItem.current === null || dragOverItem.current === null) return;
        
        let _columnOrder = [...columnOrder];
        const draggedItemContent = _columnOrder.splice(dragItem.current, 1)[0];
        _columnOrder.splice(dragOverItem.current, 0, draggedItemContent);
        
        dragItem.current = null;
        dragOverItem.current = null;
        setColumnOrder(_columnOrder);
    };


    const addBucketConfig = () => {
        const sourceField = prompt("По кое поле ще обединяваме? (Напиши ID, напр. task_type__name)");
        if (!sourceField) return;

        const newColumn = prompt("Как да се казва новата колона? (напр. 'Обединени Таскове')");
        if (!newColumn) return;

        // Добавяме празна конфигурация
        setBucketConfigs(prev => [...prev, {
            id: Date.now(),
            source_field: sourceField,
            new_column: newColumn,
            buckets: {} // Тук ще пазим { "Dev": ["QA", "Prog"] }
        }]);
    };

    const addBucketToConfig = (configId) => {
        const bucketName = prompt("Име на групата? (напр. 'Development')");
        if (!bucketName) return;

        const valuesStr = prompt(`Кои стойности влизат в '${bucketName}'? (Раздели ги със запетая, напр. Programming, QA)`);
        if (!valuesStr) return;

        const valuesArray = valuesStr.split(',').map(v => v.trim());

        setBucketConfigs(prev => prev.map(conf => {
            if (conf.id === configId) {
                return { ...conf, buckets: { ...conf.buckets, [bucketName]: valuesArray } };
            }
            return conf;
        }));
    };

    const removeBucketConfig = (configId) => {
        setBucketConfigs(prev => prev.filter(c => c.id !== configId));
    };



    useEffect(() => {
        loadMetadataForModel(baseEntity, true);
    }, [baseEntity]);

    const loadMetadataForModel = (modelName, isReset = false) => {
        setLoadingMetadata(true);
        fetch(`${URLS.aj_get_report_metadata}?model=${modelName}`)
            .then(res => res.json())
            .then(result => {
                const newFields = result.fields || [];
                if (isReset) {
                    setAvailableFields(newFields);
                    setSelectedDimensions([]);
                    setActiveFilters([]); // Reset filters too
                    setCustomSums([]);
                    setSumBuilder([]);
                    setResults([]);
                    setPivotField("");
                }
            })
            .catch(err => setError(err.message))
            .finally(() => setLoadingMetadata(false));
    };

    const handleExpand = (field) => {
        // 1. Проверяваме дали полето вече има заредени деца
        const hasChildren = availableFields.some(f => 
            f.id.startsWith(field.id + '__') && f.id !== field.id
        );

        if (hasChildren) {
            // === СВИВАНЕ (COLLAPSE) ===
            // Премахваме всички полета от масива, които започват с ID-то на родителя.
            // Използваме field.id + '__', за да хванем всички нива надолу (деца, внуци и т.н.)
            setAvailableFields(prev => prev.filter(f => !f.id.startsWith(field.id + '__')));
            
            // SMART UX: Махаме скритите полета и от избраните (Dimensions и SumBuilder)
            // За да не остават "призрачни" колони в репорта
            setSelectedDimensions(prev => prev.filter(id => !id.startsWith(field.id + '__')));
            setSumBuilder(prev => prev.filter(f => !f.id.startsWith(field.id + '__')));
            
            return; // Прекратяваме изпълнението, за да не правим нова заявка
        }

        // === РАЗГЪВАНЕ (EXPAND) ===
        if (!field.related_model) return;

        setLoadingMetadata(true);
        fetch(`${URLS.aj_get_report_metadata}?model=${field.related_model}`)
            .then(res => res.json())
            .then(result => {
                const childFields = result.fields || [];
                // Добавяме префикса на родителя
                const prefixedFields = childFields.map(child => ({
                    ...child,
                    id: `${field.id}__${child.id}`,
                    label: child.label, 
                    related_model: child.related_model
                }));

                // Записваме новите полета, като се пазим от дубликати
                setAvailableFields(prev => {
                    const existingIds = new Set(prev.map(f => f.id));
                    const uniqueNewFields = prefixedFields.filter(f => !existingIds.has(f.id));
                    return [...prev, ...uniqueNewFields];
                });
            })
            .catch(err => console.error("Грешка при разгъване:", err))
            .finally(() => setLoadingMetadata(false));
    };

    // --- FILTER LOGIC ---
    const addFilter = (field) => {
        const ops = getOperators(field.type);
        const filterUid = Date.now();
        setActiveFilters(prev => [
            ...prev,
            {
                uid: Date.now(), // unique ID for React keys
                fieldId: field.id,
                label: field.label,
                type: field.type,
                operator: ops[0].op, // Default operator
                value: '',
                isExclude: false, // НОВО: Флаг за NOT IN филтър
                availableOps: ops,
                suggestions: []
            }
        ]);

        // 2. Ако полето е текстово, питаме бекенда за уникалните стойности
        if (field.type.includes('Char') || field.type.includes('String') || field.type.includes('Text')) {
            fetch(`${URLS.aj_get_field_values}?model=${baseEntity}&field=${field.id}`)
                .then(res => res.json())
                .then(data => {
                    if (data.values) {
                        // Обновяваме само този конкретен филтър с дошлите стойности
                        setActiveFilters(prev => prev.map(f => 
                            f.uid === filterUid ? { ...f, suggestions: data.values } : f
                        ));
                    }
                })
                .catch(err => console.error("Грешка при дърпане на стойности:", err));
        }
    };

    const updateFilter = (uid, key, val) => {
        setActiveFilters(prev => prev.map(f => f.uid === uid ? { ...f, [key]: val } : f));
    };

    const removeFilter = (uid) => {
        setActiveFilters(prev => prev.filter(f => f.uid !== uid));
    };

    // --- AGGREGATION & SELECTION ---
    const toggleDimension = (id) => {
        setSelectedDimensions(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const addToSumBuilder = (field) => {
        if (!sumBuilder.find(f => f.id === field.id)) setSumBuilder(prev => [...prev, field]);
    };

    const commitSumColumn = () => {
        const finalName = tempSumName.trim() || "Общо (Total)";
        setCustomSums(prev => [...prev, { name: finalName, fields: sumBuilder.map(f => f.id) }]);
        setSumBuilder([]);
        setTempSumName(""); // Изчистваме полето
    };

    // === BUCKETING (Без prompt) ===
    const saveBucketDraft = () => {
        if (!bucketDraft.source_field || !bucketDraft.new_column) {
            alert("Моля, избери поле и въведи име на новата колона.");
            return;
        }
        
        // Трансформираме масива от buckets в обект, който бекендът очаква: { "Dev": ["QA", "Prog"] }
        const formattedBuckets = {};
        bucketDraft.buckets.forEach(b => {
            if (b.name && b.values) {
                formattedBuckets[b.name] = b.values.split(',').map(v => v.trim());
            }
        });

        setBucketConfigs(prev => [...prev, {
            id: Date.now(),
            source_field: bucketDraft.source_field,
            new_column: bucketDraft.new_column,
            buckets: formattedBuckets
        }]);
        
        // Затваряме и изчистваме формата
        setShowBucketForm(false);
        setBucketDraft({ source_field: '', new_column: '', buckets: [] });
    };

    const removeCustomSum = (idx) => {
        setCustomSums(prev => prev.filter((_, i) => i !== idx));
    };

    // --- REPORT EXECUTION ---
    const processPivot = (data) => {
        if (!pivotField) return data;
        
        // Find the metric key (the calculated/sum column)
        const metricKey = Object.keys(data[0]).find(k => k.includes('__sum') || k.includes('__avg') || customSums.some(cs => cs.name === k));
        if (!metricKey) return data;

        const pivotValues = [...new Set(data.map(item => item[pivotField]))].filter(Boolean);
        const rowKeys = Object.keys(data[0]).filter(k => k !== pivotField && k !== metricKey);
        
        const pivotedData = {};
        data.forEach(item => {
            const rowId = rowKeys.map(k => item[k]).join('|');
            if (!pivotedData[rowId]) {
                pivotedData[rowId] = {};
                rowKeys.forEach(key => pivotedData[rowId][key] = item[key]);
                pivotValues.forEach(val => pivotedData[rowId][val] = 0);
            }
            if (item[pivotField]) pivotedData[rowId][item[pivotField]] = item[metricKey];
        });
        return Object.values(pivotedData);
    };

    const runReport = () => {
        setLoadingReport(true);
        setError(null);

        // Transform UI Filters to Django Lookup format
        // Example: { "startTime__gte": "2023-01-01", "project__name__icontains": "Test" }
        const backendFilters = {};
        activeFilters.forEach(f => {
            if (f.value) {
                // НОВО: Ако е маркиран Exclude чекбокса, принудително използваме '__exclude'
                const lookupKey = f.isExclude 
                    ? `${f.fieldId}__exclude` 
                    : `${f.fieldId}__${f.operator}`;
                backendFilters[lookupKey] = f.value;
            }
        });

        // Ensure pivot field is fetched
        const dimsToSend = [...selectedDimensions];
        if (pivotField && !dimsToSend.includes(pivotField)) dimsToSend.push(pivotField);

        const payload = {
            base_entity: baseEntity,
            dimensions: dimsToSend,
            custom_sums: customSums,
            filters: backendFilters,
            bucket_dimensions: bucketConfigs,
            metrics: [] // Using custom_sums mainly
        };

        fetch(URLS.aj_generate_dynamic_report, {
            method: "POST",
            body: JSON.stringify(payload)
        })
        .then(res => res.json())
        .then(data => {
            if (data.error) throw new Error(data.error);
            const final = processPivot(data);
            setResults(final);
        })
        .catch(err => setError(err.message))
        .finally(() => setLoadingReport(false));
    };

    const rootFields = availableFields.filter(f => !f.id.includes('__'));

    // === ЕКСПОРТ КЪМ EXCEL (CSV) ===
    const exportToExcel = () => {
        if (!results || results.length === 0) return;

        // 1. Вземаме подредбата на колоните (от columnOrder, ако го ползваш, или директно от първия ред)
        const headers = Object.keys(results[0]);
        
        // Форматираме заглавията (за да изглеждат добре в Excel)
        const formattedHeaders = headers.map(k => k.replace(/__/g, ' › ').replace(/_sum/g, ' (Total)'));

        // 2. Създаваме масив с редове за CSV
        const csvRows = [];
        
        // ВАЖНО: Добавяме BOM (\uFEFF) в началото, за да може Excel да разпознае UTF-8 (кирилица)
        csvRows.push('\uFEFF' + formattedHeaders.map(h => `"${h}"`).join(','));

        // 3. Обхождаме данните и ги добавяме
        results.forEach(row => {
            const values = headers.map(header => {
                let val = row[header];
                if (val === null || val === undefined) val = '';
                
                // Ескейпваме кавичките в текста (ако има такива), за да не счупят CSV формата
                return `"${String(val).replace(/"/g, '""')}"`;
            });
            csvRows.push(values.join(','));
        });

        // 4. Създаваме Blob обект и генерираме линк за сваляне
        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        // Генерираме име на файла с днешна дата
        const dateStr = new Date().toISOString().slice(0, 10);
        link.setAttribute('download', `Dynamic_Report_${dateStr}.csv`);
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div style={{ display: 'flex', gap: '20px', fontFamily: 'Segoe UI, sans-serif', padding: '10px' }}>
            
            {/* === SIDEBAR === */}
            <div style={{ width: '400px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                
                {/* 1. Model Selection */}
                <div className="card" style={cardStyle}>
                    <label style={{ fontWeight: 'bold' }}>Base Model:</label>
                    <select 
                        value={baseEntity} 
                        onChange={e => {
                            setBaseEntity(e.target.value);
                            setSelectedPresetId(""); // Деселектираме шаблона
                            loadMetadataForModel(e.target.value, true); // Зареждаме новите полета и чистим екрана
                        }} 
                        style={{ width: '100%' }}
                    >
                        <option value="Project">Project</option>
                        <option value="Task">Task (Hours)</option>
                        <option value="Client">Client</option>
                        <option value="BrightUser">User</option>
                    </select>
                </div>

                {/* === ПАНЕЛ ЗА ШАБЛОНИ === */}
                <div className="card" style={{ ...cardStyle, background: '#e3f2fd', border: '1px solid #b6d4fe' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '10px' }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontWeight: 'bold', color: '#052c65', fontSize: '12px' }}>📂 Зареди запазен репорт:</label>
                            <select 
                                value={selectedPresetId} 
                                onChange={handleLoadPreset} 
                                style={{ width: '100%', padding: '6px', fontSize: '12px', marginTop: '4px', borderRadius: '3px', border: '1px solid #9ec5fe' }}
                            >
                                <option value="">-- Избери шаблон --</option>
                                {savedPresets.map(p => (
                                    <option key={p.id} value={p.id}>{p.name} ({p.base_entity})</option>
                                ))}
                            </select>
                        </div>
                        <button 
                            onClick={handleSavePreset} 
                            style={{ padding: '6px 12px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', height: 'fit-content' }}
                        >
                            💾 Запази
                        </button>
                    </div>
                </div>

                {/* 2. FIELD TREE */}
                <div className="card" style={{ ...cardStyle, flex: 1, overflowY: 'auto', minHeight: '250px', maxHeight: '400px' }}>
                    <div style={{ borderBottom: '1px solid #eee', paddingBottom: '5px', fontWeight: 'bold' }}>Fields</div>
                    {rootFields.map(f => (
                        <FieldTreeItem 
                            key={f.id} field={f} allFields={availableFields} 
                            onExpand={handleExpand} onToggleDim={toggleDimension} selectedDims={selectedDimensions} 
                            onAddToSum={addToSumBuilder} onAddFilter={addFilter}
                        />
                    ))}
                </div>

                {/* 3. ACTIVE FILTERS PANEL */}
                <div className="card" style={{ ...cardStyle, background: '#fff8f8', border: '1px solid #ffdcdc' }}>
                    <label style={{ fontWeight: 'bold', color: '#c00' }}>Filters</label>
                    {activeFilters.length === 0 && <div style={{fontSize: '12px', color: '#999'}}>Select 'Filter' on any field above</div>}
                    
                    {activeFilters.map(filter => (
                        <div key={filter.uid} style={{ marginTop: '8px', padding: '5px', background: 'white', border: '1px solid #eee', borderRadius: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 'bold' }}>
                                <span>{filter.label}</span>
                                <span onClick={() => removeFilter(filter.uid)} style={{ cursor: 'pointer', color: 'red' }}>×</span>
                            </div>
                            
                            {/* НОВО: Чекбокс за ИЗКЛЮЧВАНЕ (Exclude) */}
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px', fontSize: '11px', color: '#666' }}>
                                <input 
                                    type="checkbox" 
                                    id={`exclude-${filter.uid}`}
                                    checked={filter.isExclude || false}
                                    onChange={(e) => updateFilter(filter.uid, 'isExclude', e.target.checked)}
                                    style={{ margin: '0 4px 0 0', cursor: 'pointer' }}
                                />
                                <label htmlFor={`exclude-${filter.uid}`} style={{ cursor: 'pointer', color: filter.isExclude ? '#dc3545' : '#666', fontWeight: filter.isExclude ? 'bold' : 'normal' }}>
                                    Изключи тези стойности (NOT)
                                </label>
                            </div>

                            <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
                                {/* Operator Select */}
                                <select 
                                    value={filter.operator} 
                                    onChange={(e) => updateFilter(filter.uid, 'operator', e.target.value)}
                                    style={{ fontSize: '11px', width: '40%' }}
                                    disabled={filter.isExclude} // Ако е Exclude, операторът няма значение, затова го деактивираме визуално
                                >
                                    {filter.availableOps.map(op => <option key={op.op} value={op.op}>{op.label}</option>)}
                                </select>

                                {/* Value Input - НОВА ДИНАМИЧНА ЛОГИКА */}
                                {(() => {
                                    // Намираме кой е реално избраният оператор в момента
                                    const currentOp = filter.availableOps.find(op => op.op === filter.operator) || filter.availableOps[0];
                                    
                                    if (currentOp.input === 'select') {
                                        return (
                                            <select value={filter.value} onChange={(e) => updateFilter(filter.uid, 'value', e.target.value)} style={{ flex: 1 }}>
                                                <option value="">-- Избери --</option>
                                                <option value="True">True</option>
                                                <option value="False">False</option>
                                            </select>
                                        );
                                    } else {
                                        return (
                                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                <input 
                                                    type={currentOp.input} 
                                                    value={filter.value} 
                                                    onChange={(e) => updateFilter(filter.uid, 'value', e.target.value)}
                                                    placeholder={currentOp.op === 'last_n_days' ? "Брой дни (напр. 7)" : "Въведи или цъкни два пъти..."}
                                                    style={{ fontSize: '12px', width: '100%', border: filter.isExclude ? '1px solid #dc3545' : '1px solid #ccc', padding: '4px', borderRadius: '3px' }}
                                                    // ЗАКАЧАМЕ ГО ТВЪРДО:
                                                    list={`list-${filter.uid}`} 
                                                />
                                                
                                                {/* Рендираме datalist-а винаги, но го пълним само ако има suggestions */}
                                                <datalist id={`list-${filter.uid}`}>
                                                    {(filter.suggestions || []).map((sug, idx) => (
                                                        <option key={idx} value={sug} />
                                                    ))}
                                                </datalist>
                                            </div>
                                        );
                                    }
                                })()}
                            </div>
                        </div>
                    ))}
                </div>

                {/* === 4. АГРЕГАЦИЯ (СУМИРАНЕ НА ЧАСОВЕ/СТОЙНОСТИ) === */}
<div className="card" style={{ ...cardStyle, background: '#f8fdf8', border: '1px solid #c3e6cb' }}>
    <label style={{ fontWeight: 'bold', color: '#155724', display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: '16px', marginRight: '5px' }}>∑</span> Стойности (Values)
    </label>
    <p style={{ fontSize: '11px', color: '#666', margin: '5px 0 10px 0' }}>Натисни +∑ на числовите полета горе, за да ги добавиш тук.</p>
    
    {/* Буфер (Калкулатор) */}
    {sumBuilder.length > 0 && (
        <div style={{ background: '#fff', padding: '10px', border: '1px dashed #28a745', borderRadius: '4px', marginBottom: '10px' }}>
            <div style={{ marginBottom: '8px' }}>
                {sumBuilder.map((f, i) => (
                    <span key={i} style={tagStyle}>
                        {f.label} 
                        <span onClick={() => setSumBuilder(prev => prev.filter((_, idx) => idx !== i))} style={{ cursor: 'pointer', marginLeft: '4px', color: '#dc3545', fontWeight: 'bold' }}>×</span>
                    </span>
                ))}
            </div>
            
            <div style={{ display: 'flex', gap: '5px' }}>
                <input 
                    type="text" 
                    placeholder="Кръсти колоната (напр. 'Общо Часове')..." 
                    value={tempSumName}
                    onChange={(e) => setTempSumName(e.target.value)}
                    style={{ flex: 1, padding: '5px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '3px' }}
                />
                <button onClick={commitSumColumn} style={{ background: '#28a745', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                    Запази
                </button>
            </div>
        </div>
    )}

    {/* Готови суми */}
    {customSums.map((cs, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', background: '#e2e6ea', padding: '5px 8px', borderRadius: '3px', fontSize: '12px', marginTop: '5px', border: '1px solid #dae0e5' }}>
            <span><strong>{cs.name}</strong> <span style={{color: '#6c757d'}}>({cs.fields.length} полета)</span></span>
            <button onClick={() => removeCustomSum(i)} style={{ color: '#dc3545', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>×</button>
        </div>
    ))}
</div>


                {/* === 6. ОБЕДИНЯВАНЕ (BUCKETING) === */}
<div className="card" style={{ ...cardStyle, background: '#f4f6f9', border: '1px solid #cdd4dc' }}>
    <label style={{ fontWeight: 'bold', color: '#343a40' }}>🗂 Обединяване на категории</label>
    
    {!showBucketForm ? (
        <button onClick={() => setShowBucketForm(true)} style={{ width: '100%', padding: '6px', marginTop: '10px', background: '#fff', border: '1px dashed #6c757d', color: '#495057', cursor: 'pointer', borderRadius: '3px' }}>
            + Създай ново обединение
        </button>
    ) : (
        <div style={{ background: '#fff', border: '1px solid #adb5bd', padding: '10px', borderRadius: '4px', marginTop: '10px' }}>
            <label style={{ fontSize: '11px', fontWeight: 'bold' }}>
                1. Кое поле ще групираме?
            </label>
            {/* Добавяме малък жокер за потребителите */}
            <div style={{ fontSize: '10px', color: '#856404', background: '#fff3cd', padding: '3px 5px', borderRadius: '3px', marginBottom: '5px' }}>
                ℹ️ <i>Не виждаш полето? Разгъни го първо от дървото с полета горе (със стрелката ▶), за да се зареди тук.</i>
            </div>
            
            <select 
                value={bucketDraft.source_field} 
                onChange={(e) => setBucketDraft({...bucketDraft, source_field: e.target.value})}
                style={{ width: '100%', marginBottom: '10px', fontSize: '12px', padding: '4px' }}
            >
                <option value="">-- Избери поле --</option>
                {availableFields
                    // СКРИВАМЕ: Числа (Float/Integer), ID-та (AutoField) и самите РЕЛАЦИИ (is_relation), 
                    // за да останат само реалните текстови полета като 'name'
                    .filter(f => !f.type.includes('Float') && !f.type.includes('Integer') && !f.type.includes('AutoField') && !f.is_relation)
                    .map(f => (
                        <option key={f.id} value={f.id}>
                            {/* Форматираме името красиво: project › client › name */}
                            {f.id.replace(/__/g, ' › ')} 
                            {/* Показваме оригиналния лейбъл в скоби */}
                            {` (${f.label.split('->').pop().trim()})`}
                        </option>
                ))}
            </select>

            <label style={{ fontSize: '11px', fontWeight: 'bold' }}>2. Име на новата колона:</label>
            <input 
                type="text" 
                placeholder="напр. 'Етап на работа'"
                value={bucketDraft.new_column}
                onChange={(e) => setBucketDraft({...bucketDraft, new_column: e.target.value})}
                style={{ width: '100%', marginBottom: '10px', fontSize: '12px', padding: '4px', boxSizing: 'border-box' }}
            />

            <label style={{ fontSize: '11px', fontWeight: 'bold' }}>3. Дефинирай групите:</label>
            {bucketDraft.buckets.map((b, i) => (
                <div key={i} style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                    <input type="text" placeholder="Име (напр. Dev)" value={b.name} onChange={(e) => {
                        const newB = [...bucketDraft.buckets];
                        newB[i].name = e.target.value;
                        setBucketDraft({...bucketDraft, buckets: newB});
                    }} style={{ width: '30%', fontSize: '11px', padding: '3px' }}/>
                    
                    <input type="text" placeholder="Стойности (напр. PR, QA)" value={b.values} onChange={(e) => {
                        const newB = [...bucketDraft.buckets];
                        newB[i].values = e.target.value;
                        setBucketDraft({...bucketDraft, buckets: newB});
                    }} style={{ flex: 1, fontSize: '11px', padding: '3px' }}/>
                </div>
            ))}
            
            <button onClick={() => setBucketDraft({...bucketDraft, buckets: [...bucketDraft.buckets, {name: '', values: ''}]})} style={{ fontSize: '10px', background: '#e9ecef', border: '1px solid #ced4da', padding: '2px 5px', cursor: 'pointer', marginBottom: '10px' }}>
                + Добави Група
            </button>

            <div style={{ display: 'flex', gap: '5px' }}>
                <button onClick={saveBucketDraft} style={{ flex: 1, background: '#007bff', color: 'white', border: 'none', padding: '5px', borderRadius: '3px', cursor: 'pointer' }}>Запази</button>
                <button onClick={() => setShowBucketForm(false)} style={{ background: '#6c757d', color: 'white', border: 'none', padding: '5px', borderRadius: '3px', cursor: 'pointer' }}>Отказ</button>
            </div>
        </div>
    )}

    {/* Списък с готовите обединения */}
    {bucketConfigs.map(conf => (
        <div key={conf.id} style={{ background: '#fff', border: '1px solid #dee2e6', padding: '8px', borderRadius: '4px', marginTop: '5px', fontSize: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '4px', marginBottom: '4px' }}>
                <strong>{conf.new_column}</strong>
                <button onClick={() => removeBucketConfig(conf.id)} style={{ color: '#dc3545', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold' }}>×</button>
            </div>
            {Object.entries(conf.buckets).map(([bName, bVals]) => (
                <div key={bName} style={{ display: 'flex', justifyContent: 'space-between', color: '#495057' }}>
                    <span>{bName}:</span> <span style={{ color: '#007bff' }}>{bVals.join(', ')}</span>
                </div>
            ))}
        </div>
    ))}
</div>

{/* === 7. АВТОМАТИЗАЦИЯ (CELERY BEAT) === */}
                <div className="card" style={{ ...cardStyle, background: isAutomated ? '#f0f8ff' : '#f8f9fa', border: isAutomated ? '1px solid #b8daff' : '1px solid #dee2e6' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input 
                            type="checkbox" 
                            id="chk-automate"
                            checked={isAutomated} 
                            onChange={(e) => setIsAutomated(e.target.checked)} 
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <label htmlFor="chk-automate" style={{ fontWeight: 'bold', color: isAutomated ? '#004085' : '#495057', cursor: 'pointer', margin: 0 }}>
                            ⏱️ Автоматизирай репорта (Email)
                        </label>
                    </div>

                    {isAutomated && (
                        <div style={{ marginTop: '10px', fontSize: '12px' }}>
                            <label style={{ fontWeight: 'bold' }}>Получатели (имейли със запетая):</label>
                            <input 
                                type="text" 
                                placeholder="напр. ivan@bright.com, pesho@bright.com" 
                                value={autoEmails}
                                onChange={e => setAutoEmails(e.target.value)}
                                style={{ width: '100%', padding: '5px', marginBottom: '8px', border: '1px solid #ccc', borderRadius: '3px', boxSizing: 'border-box' }}
                            />

                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontWeight: 'bold' }}>Честота:</label>
                                    <select value={autoFreq} onChange={e => setAutoFreq(e.target.value)} style={{ width: '100%', padding: '5px' }}>
                                        <option value="daily">Всеки ден</option>
                                        <option value="weekly">Всяка седмица</option>
                                        <option value="monthly">Всеки месец</option>
                                    </select>
                                </div>
                                
                                <div style={{ width: '80px' }}>
                                    <label style={{ fontWeight: 'bold' }}>Час:</label>
                                    <input type="time" value={autoTime} onChange={e => setAutoTime(e.target.value)} style={{ width: '100%', padding: '4px' }} />
                                </div>
                            </div>

                            {/* Допълнителни опции според честотата */}
                            {autoFreq === 'weekly' && (
                                <div style={{ marginTop: '8px' }}>
                                    <label style={{ fontWeight: 'bold' }}>Ден от седмицата:</label>
                                    <select value={autoDayOfWeek} onChange={e => setAutoDayOfWeek(e.target.value)} style={{ width: '100%', padding: '5px' }}>
                                        <option value="1">Понеделник</option>
                                        <option value="2">Вторник</option>
                                        <option value="3">Сряда</option>
                                        <option value="4">Четвъртък</option>
                                        <option value="5">Петък</option>
                                        <option value="6">Събота</option>
                                        <option value="0">Неделя</option>
                                    </select>
                                </div>
                            )}

                            {autoFreq === 'monthly' && (
                                <div style={{ marginTop: '8px' }}>
                                    <label style={{ fontWeight: 'bold' }}>Дата от месеца:</label>
                                    <input 
                                        type="number" 
                                        min="1" max="31" 
                                        value={autoDayOfMonth} 
                                        onChange={e => setAutoDayOfMonth(e.target.value)} 
                                        style={{ width: '100%', padding: '5px', boxSizing: 'border-box' }}
                                    />
                                </div>
                            )}

                            <div style={{ marginTop: '10px', color: '#856404', fontSize: '10px', background: '#fff3cd', padding: '5px', borderRadius: '3px' }}>
                                ⚠️ <b>Внимание:</b> Увери се, че филтрите ти за дати (горе) са настроени на <i>"Last X days (Dynamic)"</i>, за да хващат винаги актуалния период, а не фиксирани дати от миналото!
                            </div>
                        </div>
                    )}
                </div>


                <button onClick={runReport} style={{ padding: '12px', background: '#28a745', color: 'white', border: 'none', borderRadius: '5px', fontWeight: 'bold', cursor: 'pointer' }}>RUN REPORT</button>
            </div>

            {/* === RESULTS === */}
            <div style={{ flex: 1, overflowX: 'auto', border: '1px solid #ddd', padding: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h3 style={{ margin: 0 }}>Резултати</h3>
                    {results.length > 0 && (
                        <button 
                            onClick={exportToExcel}
                            style={{ 
                                padding: '8px 15px', 
                                background: '#17a2b8', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px', 
                                cursor: 'pointer', 
                                fontWeight: 'bold' 
                            }}
                        >
                            💾 Свали в Excel
                        </button>
                    )}
                </div>
                {results.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ background: '#343a40', color: 'white' }}>
                                {/* Рендираме заглавията според columnOrder */}
                                {columnOrder.map((colKey, index) => (
                                    <th 
                                        key={colKey} 
                                        draggable // Правим клетката влачеща се
                                        onDragStart={(e) => dragItem.current = index}
                                        onDragEnter={(e) => dragOverItem.current = index}
                                        onDragEnd={handleColumnSort}
                                        onDragOver={(e) => e.preventDefault()} // Задължително за Drop
                                        style={{ 
                                            padding: '8px', 
                                            textAlign: 'left',
                                            cursor: 'grab', // Показваме ръчичка
                                            userSelect: 'none'
                                        }}
                                        title="Хвани и завлачи, за да преместиш колоната"
                                    >
                                        {/* Иконка за влачене */}
                                        <span style={{ marginRight: '6px', color: '#888', cursor: 'grab' }}>⋮⋮</span>
                                        {colKey.replace(/__/g, ' › ').replace(/_sum/g, ' (Total)')}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((row, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
                                    {/* Рендираме клетките В РЕДА НА columnOrder */}
                                    {columnOrder.map(colKey => {
                                        const v = row[colKey];
                                        return (
                                            <td key={colKey} style={{ padding: '6px' }}>
                                                {typeof v === 'number' ? v.toFixed(2) : (v || '-')}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : <div style={{ textAlign: 'center', marginTop: '50px', color: '#aaa' }}>Configure filters and click Run.</div>}
            </div>
        </div>
    );
};

const cardStyle = { background: 'white', padding: '10px', borderRadius: '5px', border: '1px solid #ddd', marginBottom: '0' };
const tagStyle = { display: 'inline-block', background: '#e2e6ea', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', marginRight: '4px' };

export {DynamicReportEngine}